import { useEffect, useCallback, useRef } from "react";
import { useBackgroundMusicContext } from "../../../context/BackgroundMusicContext";

const useBackgroundMusic = ({ startSimulation = true, blockPlaying = false, scenes, currentScene, playbackRate, isVideoPlaying, playedProgress }) => {

    const settingsRef = useRef({ volume: { current: 1 }, playbackRate: 1 });
    const audioTracksRef = useRef([]);

    const {
        chooseBackgroundMusic,
        changeVideoVolume,
        changeMusicVolume,
        handlePlayerPlayedMusic,
        resetBackgroundMusicContext,
    } = useBackgroundMusicContext();


    const handleStartBackgroundMusic = useCallback(() => {
        audioTracksRef?.current?.forEach(({ sceneId, track, musicId }) => {
            if (!track) {
                return;
            }
            if (currentScene && sceneId === currentScene?.id && !blockPlaying) {
                calculateMusicDuration(
                    scenes,
                    track,
                    (playedProgress - ((currentScene?.trimStart ?? 0) / 1000) / (currentScene?.playbackRate || 1)),
                    currentScene
                );

                const { volume, playbackRate } = settingsRef.current;

                if (currentScene?.music?.volume && volume?.current) {
                    track.volume = currentScene.music.volume * volume.current;
                }

                track.loop = true;
                track.playbackRate = playbackRate;
                track.play();

                handlePlayerPlayedMusic(musicId);
                return;
            } else {
                !track.paused && track.pause();
            }
        });
    }, [scenes, currentScene, playedProgress, handlePlayerPlayedMusic, playbackRate, blockPlaying]);

    const handleStopBackgroundMusic = () => {
        handlePlayerPlayedMusic(null);
        audioTracksRef?.current?.forEach(({ track, sceneId }) => {
            !track.paused && track?.pause();
        });
    };

    const calculateMusicDuration = (scenes, track, currentDuration, currentScene) => {
        let musicDuration = 0;
        let isAddToDuration = true;
        const currentSceneIndex = scenes.findIndex(item => item.id === currentScene?.id);

        // Check if the previous scene has the same music, and play a new track from the same place
        [...scenes]
            .reverse()
            .slice(scenes.length - currentSceneIndex - 1)
            .forEach((scene, index, scenesArray) => {
                if (isAddToDuration) {
                    isAddToDuration = scene?.music?.id === scenesArray[index + 1]?.music?.id;
                    const addedDuration = scenesArray[index + 1]?.trimmedDuration / (scenesArray[index + 1]?.playbackRate || 1) / 1000;
                    if (isAddToDuration && isFinite(addedDuration)) {
                        musicDuration += addedDuration;
                    }
                }
            });

        const totalDuration = musicDuration + currentDuration;

        if (track.duration > totalDuration) {
            track.currentTime = totalDuration;
        } else {
            // Since the track is looped, we need to calculate the correct value
            // if the current timing is bigger than the track duration
            const fractional = (totalDuration / track.duration) % 1;
            const calculatedTime = track.duration * fractional;
            const isFiniteValue = isFinite(calculatedTime);

            if (isFiniteValue) {
                track.currentTime = calculatedTime;
            } else {
                track.currentTime = totalDuration;
            }
        }
    };

    const onSeekCallback = useCallback(
        seekTime => {
            audioTracksRef?.current?.length && audioTracksRef?.current?.forEach(({ sceneId, track, musicId }) => {
                if (sceneId === currentScene?.id && !blockPlaying) {
                    if (track) {
                        calculateMusicDuration(
                            scenes,
                            track,
                            (seekTime - ((currentScene?.trimStart ?? 0) / 1000) / (currentScene?.playbackRate || 1)),
                            currentScene
                        );
                    }

                    if (isVideoPlaying) {
                        handlePlayerPlayedMusic(musicId);
                    } else {
                        handlePlayerPlayedMusic(null);
                    }
                } else {
                    !track.paused && track?.pause()
                }
            });
        },
        [scenes, currentScene, playedProgress, blockPlaying]
    );

    useEffect(() => {
        if (!startSimulation) {
            audioTracksRef.current.forEach(({ track }) =>
                !track.paused && track.pause());
            audioTracksRef.current = [];
            return;
        };
        scenes.forEach(({ id, music, videoVolume }) => {
            let audioTrack = audioTracksRef.current.find(({ sceneId }) => sceneId === id);
            // Skip scene, if it doesn't has music 
            if (!music) {
                if (id === currentScene?.id) {
                    const currentSceneVideoVolume = videoVolume ?? 1;
                    changeVideoVolume(currentSceneVideoVolume * 100);
                    chooseBackgroundMusic(null);
                    changeMusicVolume(50);
                    handlePlayerPlayedMusic(null);
                }
                return;
            }
            if (!audioTrack) {
                audioTracksRef.current.push({ sceneId: id, track: new Audio(music.path), musicId: music.id });
                audioTrack = audioTracksRef.current[audioTracksRef.current.length - 1];
            } else {
                // Music id changed - reset the track path
                if (audioTrack.musicId !== music.id) {
                    if (!audioTrack.track.paused) {
                        audioTrack.track.pause()
                        audioTrack.track.currentTime = 0;
                    }
                    audioTrack.track.musicId = music.id;
                    audioTrack.track.src = music.path;
                }
            }
            const { track, musicId } = audioTrack;
            if (id === currentScene?.id && !blockPlaying) {
                const currentSceneVideoVolume = currentScene.videoVolume ?? 1;
                const currentSceneMusicVolume = currentScene.music?.volume ?? 0;
                if (track) {
                    calculateMusicDuration(
                        scenes,
                        track,
                        (playedProgress - ((currentScene?.trimStart ?? 0) / 1000) / (currentScene?.playbackRate || 1)),
                        currentScene
                    );

                    const { volume, playbackRate } = settingsRef.current;

                    const musicVolume = currentSceneMusicVolume;
                    const currentVolume = volume?.current ?? 100;
                    const calculatedVolume = Math.max(0, Math.min(1, musicVolume * currentVolume));

                    if (isFinite(calculatedVolume) && track.volume !== calculatedVolume) {
                        track.volume = calculatedVolume;
                    }

                    if (isVideoPlaying) {
                        track.loop = true;
                        track.playbackRate = playbackRate;
                        track.paused && track.play();
                        handlePlayerPlayedMusic(musicId);
                    }

                    chooseBackgroundMusic(currentScene.music);
                    changeMusicVolume(isFinite(currentSceneMusicVolume) ? currentSceneMusicVolume * 100 : 0);
                } else {
                    chooseBackgroundMusic(null);
                    changeMusicVolume(0);
                    handlePlayerPlayedMusic(null);
                }
                changeVideoVolume(currentSceneVideoVolume * 100);
            } else {
                !track.paused && track?.pause();
            }
        });
        const scenesWithMusic = scenes.filter(scene => scene.music);
        // Clear tracks of the scenes that doesn't exist anymore
        if (audioTracksRef?.current?.length > scenesWithMusic.length) {
            const indexesToRemove = [];
            audioTracksRef.current.forEach((audioTrack, index) => {
                if (!scenesWithMusic.find(scene => audioTrack.sceneId === scene.id)) {
                    !audioTrack.track.paused && audioTrack.track.pause()
                    indexesToRemove.push(index);
                }
            });
            indexesToRemove.forEach(index => {
                audioTracksRef.current.splice(index, 1);
            });
        }
    }, [currentScene, scenes, startSimulation]);

    useEffect(() => {
        return () => {
            audioTracksRef?.current?.forEach(({ track }) => {
                !track.paused && track?.pause();

            });
            resetBackgroundMusicContext();
        };
    }, []);

    const onPlaybackRateChange = (playbackRate) => {
        settingsRef.current = { ...settingsRef.current, playbackRate };
        audioTracksRef?.current.forEach(({ track }) => {
            if (track) {
                track.playbackRate = playbackRate;
            }
        });
    };

    const onVolumeChange = useCallback((volume) => {
        settingsRef.current = { ...settingsRef.current, volume: { ...volume } };
        audioTracksRef?.current?.forEach(({ track = null, sceneId = null }) => {
            if (sceneId === currentScene?.id && track && volume?.current >= 0) {
                track.volume = currentScene.music.volume * volume?.current;
            }
        });
    }, [currentScene]);

    useEffect(() => {
        if (isVideoPlaying) {
            handleStartBackgroundMusic();
        } else {
            handleStopBackgroundMusic();
        }
        if (blockPlaying) {
            handleStopBackgroundMusic();
        }
    }, [isVideoPlaying, blockPlaying]);


    return { handleStartBackgroundMusic, handleStopBackgroundMusic, onSeekCallback, onVolumeChange, onPlaybackRateChange };
}

export default useBackgroundMusic;